// Kid Journal — multiple private entries per kid (was a single scratchpad).
//
// Each entry is a markdown file under the kid's per-user "documents" root in
// a `journal/` folder (filename = creation epoch, so it sorts by date). The
// title is just the first line of the entry; no folders/tags — kids don't
// need that model. Private to the kid (per-user root isolation on the
// server). Reuses the notes RPCs (read/save/list/delete) so storage, the
// sha optimistic-lock, and RAG indexing all come for free.
//
// The old single `my-notes.md` scratchpad is surfaced as an entry too so
// nothing a kid already wrote is lost.

import { useEffect, useRef, useState } from "preact/hooks";
import { api, type NotesEntry } from "../../lib/rpc";

interface KidNotesProps {
  notify: (msg: string) => void;
}

const ROOT = "documents";
const DIR = "journal";
const LEGACY_PATH = "my-notes.md";

interface JournalEntry {
  path: string;
  title: string;
  ts: number;       // for sorting + the date label
  legacy?: boolean;
}

function titleFrom(content: string): string {
  const first = (content || "").split("\n").map((l) => l.trim()).find(Boolean) || "";
  return first ? first.slice(0, 60) : "Untitled";
}

function dateLabel(ts: number): string {
  if (!ts) return "";
  try { return new Date(ts).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch { return ""; }
}

export function KidNotesView({ notify }: KidNotesProps) {
  const [entries, setEntries] = useState<JournalEntry[] | null>(null);
  const [active, setActive] = useState<string | null>(null);  // path being edited

  const loadList = async () => {
    setEntries(null);
    /** @type {JournalEntry[]} */
    const list: JournalEntry[] = [];
    // Journal folder (may not exist yet → treat as empty).
    let files: NotesEntry[] = [];
    try { files = await api.notesListDir(ROOT, DIR); } catch { files = []; }
    const md = files.filter((f) => f.type === "file" && f.name.endsWith(".md"));
    // Read content for a title/preview — kids have few entries, so the N
    // reads are cheap and make the list scannable.
    await Promise.all(md.map(async (f) => {
      const path = `${DIR}/${f.name}`;
      let content = "";
      try { content = (await api.notesRead(ROOT, path)).content || ""; } catch { /* skip */ }
      const ts = parseInt(f.name, 10) || f.mtimeMs || 0;
      list.push({ path, title: titleFrom(content), ts });
    }));
    // Legacy single scratchpad → one entry, if it still has content.
    try {
      const legacy = await api.notesRead(ROOT, LEGACY_PATH);
      if ((legacy.content || "").trim()) {
        list.push({ path: LEGACY_PATH, title: titleFrom(legacy.content), ts: legacy.mtimeMs || 1, legacy: true });
      }
    } catch { /* no legacy note */ }
    list.sort((a, b) => b.ts - a.ts);
    setEntries(list);
  };

  useEffect(() => { void loadList(); }, []);

  const newEntry = async () => {
    const path = `${DIR}/${Date.now()}.md`;
    try {
      await api.notesSave({ root: ROOT, path, content: "" });  // creates the file (+ folder)
      setActive(path);
    } catch (e: any) {
      notify(e?.message || "couldn't start a new entry");
    }
  };

  if (active !== null) {
    return <JournalEditor root={ROOT} path={active} notify={notify} onBack={() => { setActive(null); void loadList(); }} />;
  }

  return (
    <div className="kid-journal">
      <header className="kid-journal-head">
        <h2 className="kid-journal-title">My journal</h2>
        <button className="kid-journal-new" type="button" onClick={newEntry}>＋ New</button>
      </header>

      {entries === null && <div className="kid-empty">loading…</div>}
      {entries !== null && entries.length === 0 && (
        <div className="kid-journal-empty">
          <div className="kid-journal-empty-emoji">📓</div>
          <div>No entries yet. Tap <strong>New</strong> to write your first one!</div>
        </div>
      )}
      {entries?.map((e) => (
        <button key={e.path} type="button" className="kid-journal-row" onClick={() => setActive(e.path)}>
          <span className="kid-journal-row-title">{e.title}</span>
          <span className="kid-journal-row-date">{dateLabel(e.ts)}</span>
        </button>
      ))}
    </div>
  );
}

// Single-entry editor — the old scratchpad autosave logic, scoped to one
// entry path, with a back button and delete.
function JournalEditor({ root, path, notify, onBack }: { root: string; path: string; notify: (m: string) => void; onBack: () => void }) {
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const shaRef = useRef<string | undefined>(undefined);
  const saveTimerRef = useRef<number | null>(null);
  const latestRef = useRef("");

  useEffect(() => {
    let cancelled = false;
    api.notesRead(root, path)
      .then((r) => { if (cancelled) return; setContent(r.content || ""); latestRef.current = r.content || ""; shaRef.current = r.sha256; })
      .catch(() => { if (!cancelled) shaRef.current = undefined; })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [path]);

  const flush = async (next: string) => {
    setStatus("saving");
    try {
      const r = await api.notesSave({ root, path, content: next, expectedSha: shaRef.current });
      if (r.ok) {
        shaRef.current = r.sha256;
        if (latestRef.current === next) setStatus("saved");
      } else if (r.currentSha) {
        if (typeof r.currentContent === "string") { setContent(r.currentContent); latestRef.current = r.currentContent; }
        shaRef.current = r.currentSha; setStatus("idle");
        notify("Reloaded — edited somewhere else");
      } else { setStatus("error"); notify(r.error || "save failed"); }
    } catch (err) { setStatus("error"); notify((err as Error).message || "save failed"); }
  };

  const onInput = (next: string) => {
    setContent(next); latestRef.current = next; setStatus("saving");
    if (saveTimerRef.current != null) window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(() => { saveTimerRef.current = null; void flush(latestRef.current); }, 600);
  };

  // Flush a pending save when leaving the editor.
  const flushPending = () => {
    if (saveTimerRef.current != null) { window.clearTimeout(saveTimerRef.current); saveTimerRef.current = null; void flush(latestRef.current); }
  };
  useEffect(() => () => flushPending(), []);

  const del = async () => {
    if (!window.confirm("Delete this journal entry?")) return;
    if (saveTimerRef.current != null) { window.clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
    try { await api.notesDelete(root, path); onBack(); }
    catch (e: any) { notify(e?.message || "couldn't delete"); }
  };

  return (
    <div className="kid-journal kid-journal-editor">
      <header className="kid-journal-head">
        <button className="kid-journal-back" type="button" onClick={() => { flushPending(); onBack(); }}>‹ Journal</button>
        <div className={`kid-notes-status status-${status}`}>
          {status === "saving" ? "Saving…" : status === "saved" ? "Saved" : status === "error" ? "Couldn't save" : ""}
        </div>
        <button className="kid-journal-del" type="button" onClick={del} aria-label="Delete entry">🗑</button>
      </header>
      {loading ? (
        <div className="kid-empty">loading…</div>
      ) : (
        <textarea
          className="kid-notes-textarea"
          value={content}
          onInput={(e) => onInput((e.currentTarget as HTMLTextAreaElement).value)}
          placeholder="Start with a title on the first line, then write away. This is just for you."
          autoFocus
        />
      )}
    </div>
  );
}
